/**
 * Awareness landing — follow invites open in the native app.
 */
import { useState } from 'react';
import AppInstallHeadBanner from '@/components/AppInstallHeadBanner';
import { useAppInstallBanner } from '@/hooks/useAppInstallBanner';
import { attemptSchemeHandoff } from '@/services/nativeAppHandoff';
import X9Logo from '../components/X9Logo';
import StatusBadge from '../components/StatusBadge';
import type { TaskStatus } from '../types';

interface Props {
  title: string;
  status: TaskStatus;
  deepLink: string;
}

export default function AwarenessLandingStep({ title, status, deepLink }: Props) {
  const installBanner = useAppInstallBanner({ softShowOnMount: true });
  const [busy, setBusy] = useState(false);

  const handleOpenInApp = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { launched } = await attemptSchemeHandoff(deepLink);
      if (!launched) {
        installBanner.showAfterFailedHandoff();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AppInstallHeadBanner
        open={installBanner.visible}
        onDismiss={installBanner.dismiss}
      />
      <div className="execute-screen items-center justify-center px-6 pb-[max(env(safe-area-inset-bottom),16px)] text-center">
        <div className="w-[min(25vw,5.7rem)] shrink-0 text-x9-marine">
          <X9Logo title="X9" className="block w-full select-none" />
        </div>

        <div className="mt-10 min-h-[6.5rem] max-w-sm">
          <StatusBadge status={status} surface="paper" variant="filled" className="justify-center" />
          <h1 className="mt-3 text-xl font-semibold tracking-tight text-x9-ink">{title}</h1>
          <p className="mt-2 text-sm leading-relaxed text-x9-marineSoft">
            Follow this task in the X9 app to get live status updates. Install or open X9, then confirm follow.
          </p>
        </div>

        <div className="mt-10 w-full max-w-md space-y-3 transition duration-300 ease-out motion-reduce:transition-none">
          <button
            type="button"
            className="btn-paper-primary w-full"
            onClick={() => void handleOpenInApp()}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? 'Opening…' : 'Open in X9 app'}
          </button>
          <p className="text-xs text-x9-marineSoft/80">
            Following is available in the mobile app only.
          </p>
        </div>
      </div>
    </>
  );
}
